import React, { useState } from "react";
import {
    ProjectComplete,
    CompleteIntermediate,
    CompleteTask,
    MiscEmployeeTaskGroup,
    MiscEmployeeTaskItem,
    ProjectPhaseWithAccountCodes,
    ProjectPhaseAccountCode,
} from "../interfaces/ProjectCompleteInterface";
import './ProjectCompleteDisplay.css';
import DollarUtil from "../util/DollarUtil";
import Colors from "../styles/Colors.json";

interface ProjectCompleteDisplayProps {
    projectData: ProjectComplete;
}

function hasHourlyCostCodes(projectData: ProjectComplete): boolean {
    const phases = projectData.projectPhasesAccountCodes ?? [];
    return phases.some((p) => (p.accountCodes?.length ?? 0) > 0);
}

/** Sort by numeric `altAccountCodeID`; non-numeric ids sort after numeric ones, then lexically. */
function sortHourlyCostCodesByAltId(codes: ProjectPhaseAccountCode[]): ProjectPhaseAccountCode[] {
    return [...codes].sort((a, b) => {
        const sa = String(a.altAccountCodeID ?? "").trim();
        const sb = String(b.altAccountCodeID ?? "").trim();
        const na = Number(sa);
        const nb = Number(sb);
        const aNum = sa !== "" && Number.isFinite(na);
        const bNum = sb !== "" && Number.isFinite(nb);
        if (aNum && bNum && na !== nb) {
            return na - nb;
        }
        if (aNum !== bNum) {
            return aNum ? -1 : 1;
        }
        return sa.localeCompare(sb, undefined, { numeric: true });
    });
}

const ProjectCompleteDisplay: React.FC<ProjectCompleteDisplayProps> = ({ projectData }) => {
    const [visibleIntermediates, setVisibleIntermediates] = useState<Set<number>>(new Set());
    const [visibleTasks, setVisibleTasks] = useState<Set<number>>(new Set());
    const [visibleMiscAccountCodes, setVisibleMiscAccountCodes] = useState<Set<number>>(new Set());
    const [visibleHourlyPhases, setVisibleHourlyPhases] = useState<Set<number>>(new Set());

    const handleToggleIntermediateVisibility = (intermediateID: number) => {
        setVisibleIntermediates(prevState => {
            const newState = new Set(prevState);
            if (newState.has(intermediateID)) {
                newState.delete(intermediateID);
            } else {
                newState.add(intermediateID);
            }
            return newState;
        });
    };

    const handleToggleTaskVisibility = (taskID: number) => {
        setVisibleTasks(prevState => {
            const newState = new Set(prevState);
            if (newState.has(taskID)) {
                newState.delete(taskID);
            } else {
                newState.add(taskID);
            }
            return newState;
        });
    };

    const handleToggleMiscAccountCodeVisibility = (accountCodeID: number) => {
        setVisibleMiscAccountCodes(prevState => {
            const newState = new Set(prevState);
            if (newState.has(accountCodeID)) {
                newState.delete(accountCodeID);
            } else {
                newState.add(accountCodeID);
            }
            return newState;
        });
    };

    const handleToggleHourlyPhaseVisibility = (projectPhaseID: number) => {
        setVisibleHourlyPhases((prevState) => {
            const next = new Set(prevState);
            if (next.has(projectPhaseID)) next.delete(projectPhaseID);
            else next.add(projectPhaseID);
            return next;
        });
    };

    const formatDate = (dateString: string) => {
        if (!dateString) return "-";
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return "-";
            const month = (date.getUTCMonth() + 1).toString().padStart(2, "0");
            const day = date.getUTCDate().toString().padStart(2, "0");
            const year = date.getUTCFullYear();
            return `${month}/${day}/${year}`;
        } catch {
            return dateString;
        }
    };

    const formatEmployeeName = (name: { first: string; middle: string; last: string }) => {
        const parts = [name.first, name.middle, name.last].filter(Boolean);
        return parts.join(" ");
    };

    const renderTask = (task: CompleteTask, level: number = 0) => {
        const hasEmployees = task.employeesClaimed && task.employeesClaimed.length > 0;
        const isExpanded = visibleTasks.has(task.taskID);
        const isCompleted = task.remainingAmount === 0;

        return (
            <div 
                key={task.taskID} 
                className={`complete-task-box ${isCompleted ? 'complete-task-completed' : ''}`} 
                style={{ marginLeft: `${level * 20}px` }}
            >
                <div
                    className="complete-task-header"
                    onClick={() => hasEmployees && handleToggleTaskVisibility(task.taskID)}
                    style={{ cursor: hasEmployees ? 'pointer' : 'default' }}
                >
                    <div className="complete-task-info">
                        <div className="complete-task-name">
                            {task.taskInfo.name}
                        </div>
                        <div className="complete-task-details">
                            <span>Original: {DollarUtil.formatDollar(task.originalAmount)}</span>
                            <span>Remaining: {DollarUtil.formatDollar(task.remainingAmount)}</span>
                            {task.account.altAccountCodeID && (
                                <span>Account: {task.account.altAccountCodeID}</span>
                            )}
                            {hasEmployees && (
                                <span className="employee-count">
                                    {task.employeesClaimed.length} employee{task.employeesClaimed.length !== 1 ? 's' : ''} claimed
                                </span>
                            )}
                        </div>
                    </div>
                    {hasEmployees && (
                        <span className={`toggle-arrow ${isExpanded ? "open" : "closed"}`}></span>
                    )}
                </div>
                {task.taskInfo.description && (
                    <div className="complete-task-description">
                        {task.taskInfo.description}
                    </div>
                )}
                {isExpanded && hasEmployees && (
                    <div className="complete-employees-list">
                        <div className="complete-employees-header">Employees Claimed:</div>
                        {[...task.employeesClaimed]
                            .sort((a, b) => {
                                const dateA = new Date(a.entryDate).getTime();
                                const dateB = new Date(b.entryDate).getTime();
                                return dateA - dateB; // Sort ascending (oldest first)
                            })
                            .map((employee, index) => (
                                <div key={index} className="complete-employee-item">
                                    <div className="complete-employee-name">
                                        {formatEmployeeName(employee.name)} - {employee.altEmployeeID}
                                    </div>
                                    <div className="complete-employee-details">
                                        <span>Amount: {DollarUtil.formatDollar(employee.amountClaimed)}</span>
                                        <span>Date: {formatDate(employee.entryDate)}</span>
                                        {employee.weekNumber != null && (
                                            <span>Week: {employee.weekNumber}</span>
                                        )}
                                    </div>
                                </div>
                            ))}
                    </div>
                )}
            </div>
        );
    };

    // Recursive function to calculate totals from nested intermediates
    const calculateTotalsFromIntermediates = (intermediates: CompleteIntermediate[]): {
        totalTasks: number;
        remainingTasks: number;
        originalAmount: number;
        remainingAmount: number;
    } => {
        return intermediates.reduce((acc, intermediate) => {
            // Add direct task counts
            acc.totalTasks += intermediate.totalTasks;
            acc.remainingTasks += intermediate.remainingTasks;
            
            // Add amounts from direct tasks
            if (intermediate.tasks && intermediate.tasks.length > 0) {
                intermediate.tasks.forEach(task => {
                    acc.originalAmount += task.originalAmount;
                    acc.remainingAmount += task.remainingAmount;
                });
            }
            
            // Recursively add from nested intermediates
            if (intermediate.intermediates && intermediate.intermediates.length > 0) {
                const nestedTotals = calculateTotalsFromIntermediates(intermediate.intermediates);
                acc.totalTasks += nestedTotals.totalTasks;
                acc.remainingTasks += nestedTotals.remainingTasks;
                acc.originalAmount += nestedTotals.originalAmount;
                acc.remainingAmount += nestedTotals.remainingAmount;
            }
            
            return acc;
        }, { totalTasks: 0, remainingTasks: 0, originalAmount: 0, remainingAmount: 0 });
    };

    const renderIntermediate = (intermediate: CompleteIntermediate, level: number = 0) => {
        const isExpanded = visibleIntermediates.has(intermediate.intermediateID);
        const hasContent = (intermediate.tasks && intermediate.tasks.length > 0) || 
                          (intermediate.intermediates && intermediate.intermediates.length > 0);

        // Calculate totals from tasks
        const calculateTotals = (tasks: CompleteTask[]) => {
            return tasks.reduce((acc, task) => {
                acc.originalAmount += task.originalAmount;
                acc.remainingAmount += task.remainingAmount;
                return acc;
            }, { originalAmount: 0, remainingAmount: 0 });
        };

        // If intermediate has tasks, use them; otherwise calculate from child intermediates
        let totals: { originalAmount: number; remainingAmount: number };
        let displayTotalTasks: number;
        let displayRemainingTasks: number;
        
        if (intermediate.tasks && intermediate.tasks.length > 0) {
            totals = calculateTotals(intermediate.tasks);
            displayTotalTasks = intermediate.totalTasks;
            displayRemainingTasks = intermediate.remainingTasks;
        } else if (intermediate.intermediates && intermediate.intermediates.length > 0) {
            const childTotals = calculateTotalsFromIntermediates(intermediate.intermediates);
            totals = {
                originalAmount: childTotals.originalAmount,
                remainingAmount: childTotals.remainingAmount
            };
            displayTotalTasks = childTotals.totalTasks;
            displayRemainingTasks = childTotals.remainingTasks;
        } else {
            totals = { originalAmount: 0, remainingAmount: 0 };
            displayTotalTasks = intermediate.totalTasks;
            displayRemainingTasks = intermediate.remainingTasks;
        }

        const handleIntermediateClick = (e: React.MouseEvent) => {
            e.stopPropagation(); // Prevent event from bubbling up to parent intermediates
            if (hasContent) {
                handleToggleIntermediateVisibility(intermediate.intermediateID);
            }
        };

        return (
            <div key={intermediate.intermediateID} style={{ marginLeft: `${level * 20}px` }}>
                <div className="complete-intermediate-container">
                    <div
                        className="complete-intermediate-header"
                        onClick={handleIntermediateClick}
                        style={{ cursor: hasContent ? 'pointer' : 'default' }}
                    >
                        <div className="complete-intermediate-info">
                            <div className="complete-intermediate-name">
                                {intermediate.name}
                            </div>
                        <div className="complete-intermediate-details">
                            {(() => {
                                const completedTasks = displayTotalTasks - displayRemainingTasks;
                                return (
                                    <span>Completed Tasks: <strong>{completedTasks}</strong></span>
                                );
                            })()}
                            <span>Remaining Tasks: <strong>{displayRemainingTasks}</strong></span>
                            <span>Total Original Amount: <strong>{DollarUtil.formatDollar(totals.originalAmount)}</strong></span>
                            <span>Total Remaining Amount: <strong>{DollarUtil.formatDollar(totals.remainingAmount)}</strong></span>
                        </div>
                        </div>
                        {hasContent && (
                            <span className={`toggle-arrow ${isExpanded ? "open" : "closed"}`}></span>
                        )}
                    </div>
                </div>

                {isExpanded && (
                    <div className="complete-intermediate-content">
                        {intermediate.tasks && intermediate.tasks.length > 0 && (
                            <div className="complete-tasks-section">
                                {intermediate.tasks.map(task => renderTask(task, level + 1))}
                            </div>
                        )}
                        {intermediate.intermediates && intermediate.intermediates.length > 0 && (
                            <div className="complete-nested-intermediates">
                                {intermediate.intermediates.map((nestedIntermediate) => {
                                    return renderIntermediate(nestedIntermediate, level + 1);
                                })}
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    };

    const renderMiscEmployeeTask = (task: MiscEmployeeTaskItem, level: number = 1) => {
        return (
            <div key={task.miscEmployeeTaskID} style={{ marginLeft: `${level * 20}px` }}>
                <div className="complete-misc-task-item">
                    <div className="complete-misc-task-header">
                        <div className="complete-misc-task-info">
                            <div className="complete-misc-task-name">
                                {formatEmployeeName(task.employeeName)} - {task.altEmployeeID}
                            </div>
                            <div className="complete-misc-task-details">
                                <span>Amount: <strong>{DollarUtil.formatDollar(task.amountClaimed)}</strong></span>
                                <span>Date: {formatDate(task.entryDate)}</span>
                                {task.weekNumber != null && (
                                    <span>Week: {task.weekNumber}</span>
                                )}
                                {task.intermediateName && (
                                    <span>{task.intermediateName}</span>
                                )}
                            </div>
                        </div>
                    </div>
                    {task.miscDescription && (
                        <div className="complete-misc-task-description">
                            <strong>Description:</strong> {task.miscDescription}
                        </div>
                    )}
                    {task.superDescription && (
                        <div className="complete-misc-task-description">
                            <strong>Super Description:</strong> {task.superDescription}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const renderMiscEmployeeTaskGroup = (group: MiscEmployeeTaskGroup) => {
        const isExpanded = visibleMiscAccountCodes.has(group.accountCode.accountCodeID);
        const hasTasks = group.tasks && group.tasks.length > 0;
        
        // Calculate total amount for this account code
        const totalAmount = hasTasks 
            ? group.tasks.reduce((sum, task) => sum + task.amountClaimed, 0)
            : 0;

        // Sort tasks by date (ascending - oldest first)
        const sortedTasks = hasTasks
            ? [...group.tasks].sort((a, b) => {
                const dateA = new Date(a.entryDate).getTime();
                const dateB = new Date(b.entryDate).getTime();
                return dateA - dateB;
            })
            : [];

        const handleAccountCodeClick = (e: React.MouseEvent) => {
            e.stopPropagation();
            if (hasTasks) {
                handleToggleMiscAccountCodeVisibility(group.accountCode.accountCodeID);
            }
        };

        return (
            <div key={group.accountCode.accountCodeID} style={{ marginLeft: '0px' }}>
                <div className="complete-intermediate-container" style={{ backgroundColor: Colors.Misc }}>
                    <div
                        className="complete-intermediate-header"
                        onClick={handleAccountCodeClick}
                        style={{ cursor: hasTasks ? 'pointer' : 'default' }}
                    >
                        <div className="complete-intermediate-info">
                            <div className="complete-intermediate-name">
                                {group.accountCode.altAccountCodeID} - {group.accountCode.name}
                            </div>
                            <div className="complete-intermediate-details" style={{ color: '#000000' }}>
                                <span>Total Amount: <strong>{DollarUtil.formatDollar(totalAmount)}</strong></span>
                            </div>
                        </div>
                        {hasTasks && (
                            <span className={`toggle-arrow ${isExpanded ? "open" : "closed"}`}></span>
                        )}
                    </div>
                </div>

                {isExpanded && hasTasks && (
                    <div>
                        {sortedTasks.map(task => renderMiscEmployeeTask(task, 1))}
                    </div>
                )}
            </div>
        );
    };

    const renderHourlyCostCodeRow = (code: ProjectPhaseAccountCode) => {
        const desc = code.description != null && String(code.description).trim() !== ""
            ? code.description
            : null;
        return (
            <div key={code.accountCodeID} className="complete-task-box complete-hourly-cost-code-row">
                <div className="complete-task-name">
                    {code.altAccountCodeID} - {code.name}
                </div>
                {desc != null && (
                    <div className="complete-task-description">{desc}</div>
                )}
            </div>
        );
    };

    const renderHourlyCostPhase = (phase: ProjectPhaseWithAccountCodes) => {
        const codes = sortHourlyCostCodesByAltId(phase.accountCodes ?? []);
        const isExpanded = visibleHourlyPhases.has(phase.projectPhaseID);
        const handlePhaseClick = (e: React.MouseEvent) => {
            e.stopPropagation();
            handleToggleHourlyPhaseVisibility(phase.projectPhaseID);
        };
        return (
            <div key={phase.projectPhaseID}>
                <div className="complete-intermediate-container">
                    <div
                        className="complete-intermediate-header"
                        onClick={handlePhaseClick}
                        style={{ cursor: "pointer" }}
                    >
                        <div className="complete-intermediate-info">
                            <div className="complete-intermediate-name">{phase.name}</div>
                            <div className="complete-intermediate-details">
                                {phase.altProjectPhaseID && (
                                    <span>Phase ID: <strong>{phase.altProjectPhaseID}</strong></span>
                                )}
                                <span>Cost codes: <strong>{codes.length}</strong></span>
                            </div>
                        </div>
                        <span className={`toggle-arrow ${isExpanded ? "open" : "closed"}`} />
                    </div>
                </div>
                {isExpanded && (
                    <div className="complete-hourly-codes-list">
                        {codes.map((code) => renderHourlyCostCodeRow(code))}
                    </div>
                )}
            </div>
        );
    };

    const phasesWithHourlyCodes =
        (projectData.projectPhasesAccountCodes ?? []).filter(
            (p) => (p.accountCodes?.length ?? 0) > 0
        );

    const hasTaskSections =
        (projectData.directTasks?.length ?? 0) > 0 ||
        (projectData.intermediates?.length ?? 0) > 0 ||
        (projectData.miscEmployeeTasks?.length ?? 0) > 0;

    const showTasksBox = hasTaskSections || !hasHourlyCostCodes(projectData);

    return (
        <div className="project-complete-container">
            {showTasksBox && (
                <div className="project-complete-box">
                    {/* Direct Tasks Section */}
                    {projectData.directTasks && projectData.directTasks.length > 0 && (
                        <div className="complete-direct-tasks-section">
                            <h3 className="complete-section-title">Direct Tasks</h3>
                            {projectData.directTasks.map(task => renderTask(task, 0))}
                        </div>
                    )}

                    {/* Intermediates Section */}
                    {projectData.intermediates && projectData.intermediates.length > 0 && (
                        <div className="complete-intermediates-section">
                            {projectData.intermediates.map(intermediate => renderIntermediate(intermediate, 0))}
                        </div>
                    )}

                    {/* Miscellaneous Tasks Section */}
                    {projectData.miscEmployeeTasks && projectData.miscEmployeeTasks.length > 0 && (
                        <div className="complete-misc-tasks-section">
                            <h3 className="complete-section-title" style={{ marginLeft: '-15px', paddingLeft: '15px' }}>Miscellaneous Tasks</h3>
                            {projectData.miscEmployeeTasks.map(group => renderMiscEmployeeTaskGroup(group))}
                        </div>
                    )}

                    {/* Empty State */}
                    {!hasTaskSections && !hasHourlyCostCodes(projectData) && (
                        <div className="complete-empty-state">
                            No tasks found for this project.
                        </div>
                    )}
                </div>
            )}

            {hasHourlyCostCodes(projectData) && (
                <div className="project-complete-box project-complete-hourly-outer">
                    <div className="complete-hourly-cost-codes-section">
                        <h3 className="complete-section-title">Hourly Cost Codes</h3>
                        {phasesWithHourlyCodes.map((phase) => renderHourlyCostPhase(phase))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProjectCompleteDisplay;

